import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
import type { AdminPageProps } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { KPI_BASE, agingBaseline, approvalDays, categoryBaseline, divisionBaseline, monthlyCollection, monthlySeizures, reasonBaseline, stationBaseline } from '../../data/charts';
import { PageHeader } from '../../components/admin/PageHeader';
import { ChartPanel, CHART } from '../../components/admin/ChartPanel';
import { DhakaMap } from '../../components/shared/DhakaMap';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { isPaid, needsVerification, stageInfo } from '../../utils/caseStatus';
import { bn, daysBetween, fmtDate, isDemoToday, taka } from '../../utils/bn';
const STATUS_FILTERS = [{
  id: 'all',
  label: 'সকল অবস্থা'
}, {
  id: 'active',
  label: 'চলমান'
}, {
  id: 'at_station',
  label: 'স্টেশনে রয়েছে'
}, {
  id: 'released',
  label: 'অবমুক্ত'
}];
const shortDiv = (d: string) => d.replace('ট্রাফিক-', '').replace(' বিভাগ', '');
const fmtK = (n: number) => bn(n >= 100000 ? `${(n / 100000).toFixed(1)} লাখ` : n.toLocaleString('en-IN'));
export function Dashboard({
  go,
  openCase
}: AdminPageProps) {
  const {
    cases,
    config
  } = useStore();
  const [range, setRange] = useState('6');
  const [division, setDivision] = useState('');
  const [station, setStation] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState<string | undefined>();
  const filtered = useMemo(() => cases.filter(c => (!division || c.division === division) && (!station || (c.station?.station ?? c.assignedStation) === station) && (!category || c.category === category) && (status === 'all' || (status === 'active' ? c.stage !== 'released' : c.stage === status))), [cases, division, station, category, status]);
  const live = {
    atStation: cases.filter(c => c.stage === 'at_station').length,
    today: cases.filter(c => isDemoToday(c.createdAt)).length,
    releasedToday: cases.filter(c => c.stage === 'released' && isDemoToday(c.letter?.redeemedAt)).length,
    collected: cases.filter(isPaid).reduce((s, c) => s + c.fine + c.extraCharge, 0),
    long: cases.filter(c => c.stage === 'at_station' && daysBetween(c.createdAt) > 7).length
  };
  const totalAtStation = Object.values(stationBaseline).reduce((a, b) => a + b, 0) + live.atStation;
  const queues = [{
    label: 'কাগজপত্র যাচাই অপেক্ষমাণ',
    value: filtered.filter(needsVerification).length,
    page: 'verification' as const,
    tone: 'text-warning-700'
  }, {
    label: 'পরিশোধ অপেক্ষমাণ',
    value: filtered.filter(c => c.stage === 'at_station' && !isPaid(c)).length,
    page: 'payments' as const,
    tone: 'text-warning-700'
  }, {
    label: 'অনুমোদন অপেক্ষমাণ',
    value: filtered.filter(c => c.approval === 'pending').length,
    page: 'approval' as const,
    tone: 'text-navy'
  }, {
    label: 'অবমুক্তির অনুমোদনপ্রাপ্ত',
    value: filtered.filter(c => c.letter?.status === 'issued').length,
    page: 'letters' as const,
    tone: 'text-success'
  }];
  const months = monthlySeizures.slice(-Number(range));
  const collection = monthlyCollection.slice(-Number(range));
  const catData = config.categories.map(c => ({
    name: c.label,
    key: c.key,
    value: categoryBaseline[c.key] + cases.filter(x => x.category === c.key).length
  }));
  const reasonData = Object.entries(reasonBaseline).map(([k, v]) => ({
    name: k.split(' / ')[0],
    value: v + cases.filter(c => c.reason === k).length
  })).sort((a, b) => b.value - a.value);
  const divData = Object.entries(divisionBaseline).map(([k, v]) => ({
    name: shortDiv(k),
    full: k,
    value: v + cases.filter(c => c.division === k).length
  }));
  const stationData = config.stations.filter(s => !station || s.name === station).map(s => ({
    name: s.short,
    full: s.name,
    value: stationBaseline[s.name] + cases.filter(c => c.stage === 'at_station' && c.station?.station === s.name).length,
    capacity: s.capacity
  }));
  const aging = agingBaseline.map((a, i) => ({
    ...a,
    count: a.count + (i === 0 ? live.atStation : 0)
  }));
  const markers = [...filtered.filter(c => c.stage !== 'released').map(c => ({
    id: c.pcr,
    x: c.location.x,
    y: c.location.y,
    label: c.pcr,
    kind: 'custody' as const
  })), ...config.stations.map(s => ({
    id: s.name,
    x: s.location.x,
    y: s.location.y,
    label: s.short,
    kind: 'station' as const,
    count: stationData.find(d => d.full === s.name)?.value ?? stationBaseline[s.name]
  }))];
  const sel = cases.find(c => c.pcr === selected);
  const reset = () => {
    setDivision('');
    setStation('');
    setCategory('');
    setStatus('all');
    setRange('6');
  };
  return <>
      <PageHeader title="নির্বাহী ড্যাশবোর্ড" crumbs={[]} description="ডিএমপি জুড়ে যানবাহন হেফাজত, অবমুক্তি ও আদায়ের সার্বিক চিত্র" data-id="element-612" data-mp="mguafzmrqdmp" />

      <div className="card mb-5 flex flex-wrap items-end gap-3 p-3" data-id="element-613" data-mp="2y57ep14wco49">
        <Filter label="সময়কাল" value={range} onChange={setRange} options={[['3', 'শেষ ৩ মাস'], ['6', 'শেষ ৬ মাস']]} data-id="element-614" data-mp="325ht926r8muq" />
        <Filter label="ট্রাফিক বিভাগ" value={division} onChange={setDivision} options={[['', 'সকল বিভাগ'], ...config.divisions.map(d => [d.name, shortDiv(d.name)] as [string, string])]} data-id="element-615" data-mp="374m7i78jdarx" />
        <Filter label="ডাম্পিং স্টেশন" value={station} onChange={setStation} options={[['', 'সকল স্টেশন'], ...config.stations.map(s => [s.name, s.short] as [string, string])]} data-id="element-616" data-mp="3gv73dmwts175" />
        <Filter label="যানবাহনের ধরন" value={category} onChange={setCategory} options={[['', 'সকল ধরন'], ...config.categories.map(c => [c.key, c.label] as [string, string])]} data-id="element-617" data-mp="3pvln4hu5588j" />
        <Filter label="অবস্থা" value={status} onChange={setStatus} options={STATUS_FILTERS.map(s => [s.id, s.label] as [string, string])} data-id="element-618" data-mp="3us2my9apdb1g" />
        <button className="btn btn-ghost btn-sm" onClick={reset} data-id="element-619" data-mp="20fm8wmbo0sm1"><RotateCcwIcon className="h-3.5 w-3.5" data-id="element-620" data-mp="20i8qzfw6mqii" /> রিসেট</button>
      </div>

      <section className="mb-5 grid gap-5 xl:grid-cols-[1.35fr_1fr]" data-id="element-621" data-mp="33ruxbbwtdelk">
        <div className="card overflow-hidden" data-id="element-622" data-mp="2yt03lexb65mo">
          <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-y divide-line" data-id="element-623" data-mp="2yub7deqr7dds">
            <Stat big label="মোট আটককৃত যানবাহন (২০২৬)" value={bn((KPI_BASE.totalSeized + cases.length).toLocaleString('en-IN'))} data-id="element-624" data-mp="3cpwvljp5xt55" />
            <Stat big label="বর্তমানে ডাম্পিংয়ে রয়েছে" value={bn(totalAtStation)} note={`ধারণক্ষমতা ${bn(config.stations.reduce((s, x) => s + x.capacity, 0))}`} data-id="element-625" data-mp="3mpxiffm8qre3" />
            <Stat big label="মোট আদায়কৃত অর্থ" value={`৳${fmtK(KPI_BASE.collected + live.collected)}`} note="নমুনা পরিমাণ" data-id="element-626" data-mp="3hquexzlwst88" />
            <Stat label="আজকের নতুন PCR" value={bn(KPI_BASE.todayNew + live.today)} data-id="element-627" data-mp="3rgvyqf78bphc" />
            <Stat label="আজকের অবমুক্তি" value={bn(KPI_BASE.todayReleased + live.releasedToday)} data-id="element-628" data-mp="3rj9ukq9qh5py" />
            <Stat label="দীর্ঘদিন ধরে অনিষ্পন্ন (৩০+ দিন)" value={bn(KPI_BASE.longPending + live.long)} tone="text-danger" onClick={() => go('reports')} data-id="element-629" data-mp="98y78xeovikv" />
          </div>
        </div>
        <div className="card p-4" data-id="element-630" data-mp="nyreg6lm79mo">
          <div className="mb-2 flex items-center justify-between" data-id="element-631" data-mp="swsxwsmydayo">
            <h2 className="text-sm font-bold text-ink" data-id="element-632" data-mp="sv0268dvob5g">চলমান কার্যপ্রবাহ</h2>
            <span className="text-[11px] text-ink-soft" data-id="element-633" data-mp="dzadg2hb5jx1">ডেমো কেস রেকর্ড · লাইভ</span>
          </div>
          <ul className="divide-y divide-line" data-id="element-634" data-mp="ixbukhv6b95v">
            {queues.map(q => <li key={q.label} data-id="element-635" data-mp="56gt3qn07ucb">
                <button onClick={() => go(q.page)} className="group flex w-full items-center justify-between py-3 text-left" data-id="element-636" data-mp="55f5ynsprhe8">
                  <span className="text-sm text-ink" data-id="element-637" data-mp="3reykukvezkz5">{q.label}</span>
                  <span className="flex items-center gap-3" data-id="element-638" data-mp="3rclt5fzutw8w">
                    <span className={`text-2xl font-bold ${q.tone}`} data-id="element-639" data-mp="9gozl9pt1nu">{bn(q.value)}</span>
                    <ArrowRightIcon className="h-4 w-4 text-ink-soft transition-transform duration-150 group-hover:translate-x-0.5" data-id="element-640" data-mp="3idrznyhtllzk" />
                  </span>
                </button>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="mb-5 grid gap-5 xl:grid-cols-[1fr_1fr]" data-id="element-641" data-mp="6ne4xdd5jg3b">
        <ChartPanel title="ঢাকা মহানগর — হেফাজত ও ডাম্পিং স্টেশন" subtitle={division ? `নির্বাচিত: ${division}` : 'লাল: হেফাজত স্থান · সবুজ: স্টেশন (যানবাহন সংখ্যা)'} data-id="element-642" data-mp="1r6w9ctdfqho">
          <DhakaMap className="h-[340px]" markers={markers} selectedId={selected} onSelect={setSelected} highlightArea={division ? shortDiv(division) : undefined} data-id="element-643" data-mp="3nyna7utdsgj3" />
          {sel && <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-canvas p-3" data-id="element-644" data-mp="3f5tp3gr96mxb">
              <div className="min-w-0" data-id="element-645" data-mp="3a5xnfo6137kx">
                <p className="font-mono text-sm font-bold text-navy" data-id="element-646" data-mp="3a6yyhsn1nu2n">{sel.pcr}</p>
                <p className="truncate text-xs text-ink-muted" data-id="element-647" data-mp="358falmow487n">{sel.regNo} · {sel.road} · {fmtDate(sel.createdAt)}</p>
              </div>
              <StatusBadge label={stageInfo(sel).label} tone={stageInfo(sel).tone} size="sm" data-id="element-648" data-mp="30adsb3zagcx8" />
              <button className="btn btn-sm btn-outline" onClick={() => openCase(sel.pcr)} data-id="element-649" data-mp="2w7hedqkp14zm">খুলুন</button>
            </div>}
        </ChartPanel>
        <ChartPanel title="মাসভিত্তিক যানবাহন আটক ও অবমুক্তি" subtitle="অক্টোবর: চলতি মাস (আংশিক)" data-id="element-650" data-mp="1fph5hmu0wril">
          <div className="h-[340px]" data-id="element-651" data-mp="1kp24tyd3v1vu">
            <ResponsiveContainer data-id="element-652" data-mp="1kq4rfu3eai5y">
              <BarChart data={months} barGap={2} data-id="element-653" data-mp="1po6c2jggypjj">
                <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-654" data-mp="1pnwkirfs0abq" />
                <XAxis dataKey="month" tick={CHART.tick} axisLine={false} tickLine={false} data-id="element-655" data-mp="s1fqbv2cacu7" />
                <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} data-id="element-656" data-mp="wz7ov7xhox7d" />
                <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-657" data-mp="11ykbpsod249y" />
                <Legend wrapperStyle={{
                fontFamily: 'Hind Siliguri',
                fontSize: 12
              }} data-id="element-658" data-mp="16q2g6qplemzo" />
                <Bar dataKey="seized" name="আটক" fill={CHART.navy} radius={[4, 4, 0, 0]} data-id="element-659" data-mp="9ag4v3u1zl5o" />
                <Bar dataKey="released" name="অবমুক্ত" fill={CHART.green} radius={[4, 4, 0, 0]} data-id="element-660" data-mp="e99j4yh5zg6w" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartPanel>
      </section>

      <section className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3" data-id="element-661" data-mp="1pf9xexho034">
        <ChartPanel title="যানবাহনের ধরন অনুযায়ী আটক" data-id="element-662" data-mp="1rs2sn4sbuuy">
          <div className="h-56" data-id="element-663" data-mp="6ptl35udzq5d"><ResponsiveContainer data-id="element-664" data-mp="6pbgnlxkm5md">
            <BarChart data={catData} data-id="element-665" data-mp="6mxobku9z7ii">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-666" data-mp="bo4vpisia4on" />
              <XAxis dataKey="name" tick={{
                ...CHART.tick,
                fontSize: 10
              }} interval={0} axisLine={false} tickLine={false} data-id="element-667" data-mp="3a5ilhvrq28wq" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={36} data-id="element-668" data-mp="3f3sfi31pn3gr" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-669" data-mp="3k2n63r5x5wbf" />
              <Bar dataKey="value" name="আটক" radius={[4, 4, 0, 0]} data-id="element-670" data-mp="3oun0z574tz15">
                {catData.map(d => <Cell key={d.key} fill={!category || category === d.key ? CHART.navy : CHART.sky} data-id="element-671" data-mp="2rfiz5gn9n7x0" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="কারণভিত্তিক আটক" data-id="element-672" data-mp="1djhlb3wr1abi">
          <div className="h-56" data-id="element-673" data-mp="1di6hj43b02ke"><ResponsiveContainer data-id="element-674" data-mp="1dgn19dhndrim">
            <BarChart data={reasonData} layout="vertical" margin={{
              left: 8
            }} data-id="element-675" data-mp="19h4hil1kbmhd">
              <XAxis type="number" hide data-id="element-676" data-mp="14i1a4ty41468" />
              <YAxis type="category" dataKey="name" tick={{
                ...CHART.tick,
                fontSize: 10
              }} width={128} axisLine={false} tickLine={false} data-id="element-677" data-mp="14gzxwm5cybmx" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-678" data-mp="zlkllyn00nsm" />
              <Bar dataKey="value" name="আটক" fill={CHART.green} radius={[0, 4, 4, 0]} barSize={12} data-id="element-679" data-mp="um80pjfotqso" />
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="বিভাগভিত্তিক PCR" subtitle="বারে ক্লিক করে বিভাগ নির্বাচন করুন" data-id="element-680" data-mp="lsbfq7y9ekeu">
          <div className="h-56" data-id="element-681" data-mp="gu0akaiwsy2y"><ResponsiveContainer data-id="element-682" data-mp="gqm4osb1e6qr">
            <BarChart data={divData} data-id="element-683" data-mp="btchljb4e763">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-684" data-mp="bsawem0e44an" />
              <XAxis dataKey="name" tick={{
                ...CHART.tick,
                fontSize: 10
              }} interval={0} axisLine={false} tickLine={false} data-id="element-685" data-mp="6u10282xixng" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={36} data-id="element-686" data-mp="3tb9szoc6b599" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-687" data-mp="27kzvsxyum0o" />
              <Bar dataKey="value" name="PCR" radius={[4, 4, 0, 0]} cursor="pointer" onClick={d => {
                const full = (d as unknown as {
                  full: string;
                }).full;
                setDivision(division === full ? '' : full);
              }} data-id="element-688" data-mp="3kcy17izkm1f8">
                {divData.map(d => <Cell key={d.full} fill={!division || division === d.full ? CHART.navy : CHART.sky} data-id="element-689" data-mp="fzhqfsjzknuy" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="মাসভিত্তিক জরিমানা আদায়" subtitle="নমুনা পরিমাণ (৳)" data-id="element-690" data-mp="730ein3ssly5">
          <div className="h-56" data-id="element-691" data-mp="b5551cyy92gk"><ResponsiveContainer data-id="element-692" data-mp="b4lo9zl1ck44">
            <LineChart data={collection} data-id="element-693" data-mp="yvr1s83i70b9">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-694" data-mp="yupri5ubahul" />
              <XAxis dataKey="month" tick={CHART.tick} axisLine={false} tickLine={false} data-id="element-695" data-mp="13n8zmna8cks0" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => fmtK(v)} width={52} data-id="element-696" data-mp="2ocwf1ysf21cd" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => taka(v)} data-id="element-697" data-mp="2jda11n4rkh2i" />
              <Line type="monotone" dataKey="amount" name="আদায়" stroke={CHART.navy} strokeWidth={2} dot={{
                r: 3
              }} data-id="element-698" data-mp="2y3h876pbmdnw" />
              <Line type="monotone" dataKey="reconciled" name="সমন্বিত" stroke={CHART.green} strokeWidth={2} dot={{
                r: 3
              }} data-id="element-699" data-mp="2t5focjk2mabd" />
            </LineChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="ডাম্পিং স্টেশনভিত্তিক অবস্থান" subtitle="বর্তমান যানবাহন বনাম ধারণক্ষমতা" data-id="element-700" data-mp="20h0bo3oew437">
          <ul className="space-y-3 pt-1" data-id="element-701" data-mp="2feaw42j18kb2">
            {stationData.map(s => {
            const pct = Math.round(s.value / s.capacity * 100);
            return <li key={s.full} data-id="element-702" data-mp="3qqtwblb4qtjb">
                  <div className="flex justify-between text-xs" data-id="element-703" data-mp="3qtzkmybxprdz"><span className="font-semibold text-ink" data-id="element-704" data-mp="3ltbjvkxwlw6k">{s.full}</span><span className="text-ink-muted" data-id="element-705" data-mp="3luwetbo4rh4y">{bn(s.value)} / {bn(s.capacity)} · {bn(pct)}%</span></div>
                  <div className="mt-1 h-2.5 rounded-full bg-canvas" data-id="element-706" data-mp="1ve15z4c0t1yz"><div className={`h-2.5 rounded-full ${pct > 85 ? 'bg-danger' : pct > 65 ? 'bg-warning' : 'bg-bdgreen'}`} style={{
                  width: `${Math.min(100, pct)}%`
                }} data-id="element-707" data-mp="20bsxvyco0o36" /></div>
                </li>;
          })}
          </ul>
        </ChartPanel>
        <ChartPanel title="অনুমোদনে গড় সময় (দিন)" data-id="element-708" data-mp="2eak99d057vnm">
          <div className="h-56" data-id="element-709" data-mp="2e9raozbc8cgk"><ResponsiveContainer data-id="element-710" data-mp="2eaajnqj0fqig">
            <BarChart data={approvalDays} data-id="element-711" data-mp="2j9dn56jcdoob">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-712" data-mp="2j8c8ks34ak1u" />
              <XAxis dataKey="division" tick={{
                ...CHART.tick,
                fontSize: 10
              }} interval={0} axisLine={false} tickLine={false} data-id="element-713" data-mp="2nzu3ayejroed" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={28} data-id="element-714" data-mp="2s0nqtqo2v16q" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => `${bn(v)} দিন`} data-id="element-715" data-mp="2x2v7uunrl7fx" />
              <Bar dataKey="days" name="গড় দিন" radius={[4, 4, 0, 0]} data-id="element-716" data-mp="320n0jqw8gxl6">
                {approvalDays.map(d => <Cell key={d.division} fill={d.days > 4 ? CHART.warning : CHART.navy} data-id="element-717" data-mp="3c5mshkcom8v3" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
        <ChartPanel title="দীর্ঘদিনের অনিষ্পন্ন মামলা" subtitle="স্টেশনে অবস্থানকাল অনুযায়ী" className="lg:col-span-2 xl:col-span-3" data-id="element-718" data-mp="326nle67kela7">
          <div className="h-48" data-id="element-719" data-mp="2y6n8iq046i59"><ResponsiveContainer data-id="element-720" data-mp="3uu9yc52on29q">
            <BarChart data={aging} data-id="element-721" data-mp="3ut8at14hcnb4">
              <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-722" data-mp="3uwnvcjgxaojx" />
              <XAxis dataKey="bucket" tick={CHART.tick} axisLine={false} tickLine={false} data-id="element-723" data-mp="3pwrma5tdc6xi" />
              <YAxis tick={CHART.tick} axisLine={false} tickLine={false} tickFormatter={v => bn(v)} width={32} data-id="element-724" data-mp="3l3fc30pfs2oa" />
              <Tooltip contentStyle={CHART.tooltip} formatter={(v: number) => bn(v)} data-id="element-725" data-mp="3h2mzpyl9phns" />
              <Bar dataKey="count" name="যানবাহন" radius={[4, 4, 0, 0]} data-id="element-726" data-mp="hggc923revc8">
                {aging.map((a, i) => <Cell key={a.bucket} fill={i >= 3 ? CHART.danger : i === 2 ? CHART.warning : CHART.navy} data-id="element-727" data-mp="co51t5be7r5f" data-mpi={i} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer></div>
        </ChartPanel>
      </section>
    </>;
}
function Stat({
  label,
  value,
  note,
  big,
  tone,
  onClick
}: {
  label: string;
  value: string;
  note?: string;
  big?: boolean;
  tone?: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return <Tag onClick={onClick} className={`p-4 text-left ${onClick ? 'hover:bg-navy-50' : ''}`} data-id="element-728" data-mp="1o6aovjx5a589">
      <p className="text-xs text-ink-muted" data-id="element-729" data-mp="1o8nfelgyxnwx">{label}</p>
      <p className={`${big ? 'text-3xl' : 'text-2xl'} font-bold ${tone ?? 'text-navy'}`} data-id="element-730" data-mp="1t65g5oku0wtu">{value}</p>
      {note && <p className="text-[11px] text-ink-soft" data-id="element-731" data-mp="1e9o40v4tje68">{note}</p>}
    </Tag>;
}
function Filter({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return <label className="flex min-w-[140px] flex-col text-xs font-semibold text-ink-muted" data-id="element-732" data-mp="38kb8atxy4w9f">
      {label}
      <select className="input mt-1 h-9" value={value} onChange={e => onChange(e.target.value)} data-id="element-733" data-mp="11xvi889d2qdy">
        {options.map(([v, l]) => <option key={v} value={v} data-id="element-734" data-mp="wzaq6w49jd0l">{l}</option>)}
      </select>
    </label>;
}